import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FaSignOutAlt, FaSchool, FaKey, FaTimes } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { authApi } from '../../api/client';

export default function Sidebar({ items, title }) {
  const { school, user, logout } = useAuth();
  const navigate = useNavigate();
  const [showChangePass, setShowChangePass] = useState(false);
  const [passForm, setPassForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);

  const primary = school?.primary_color || '#0d6efd';
  document.documentElement.style.setProperty('--school-primary', primary);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out');
    navigate('/');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passForm.new_password !== passForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSaving(true);
    try {
      await authApi.changePassword({
        current_password: passForm.current_password,
        new_password: passForm.new_password,
      });
      toast.success('Password changed successfully');
      setShowChangePass(false);
      setPassForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally { setSaving(false); }
  };

  return (
    <>
      <div className="sms-sidebar">
        {/* Logo */}
        <div className="sidebar-logo">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {school?.logo
              ? <img src={school.logo} alt="logo" style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'cover' }} />
              : <div style={{ width: 44, height: 44, borderRadius: 10, background: primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FaSchool color="#fff" size={22} />
                </div>
            }
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#fff', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {school?.name || title || 'School SMS'}
              </div>
              <div className="sidebar-school">{user?.role?.toUpperCase()}</div>
            </div>
          </div>
        </div>

        {/* Nav items — scrollable */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {items.map((item, i) => {
            if (item.type === 'section') {
              return <div key={i} className="nav-section">{item.label}</div>;
            }
            return (
              <NavLink
                key={i}
                to={item.to}
                end={item.to === '/admin' || item.to === '/teacher' || item.to === '/learner' || item.to === '/parent' || item.to === '/super-admin'}
                className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              >
                {item.icon && <item.icon size={16} />}
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* Bottom — always visible, sticky */}
        <div className="sms-sidebar-bottom">
          {/* User info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', marginBottom: 4 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: primary, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '0.88rem', flexShrink: 0 }}>
              {user?.first_name?.[0]}{user?.surname?.[0]}
            </div>
            <div style={{ overflow: 'hidden', flex: 1 }}>
              <div style={{ fontSize: '0.83rem', fontWeight: 600, color: '#e0e0e0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.name || `${user?.first_name} ${user?.surname}`}
              </div>
              <div style={{ fontSize: '0.7rem', color: '#8899aa', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.email}</div>
            </div>
          </div>

          {/* Change password */}
          <button
            onClick={() => setShowChangePass(true)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'none', border: 'none', color: '#b0b8cc', cursor: 'pointer', borderRadius: 6, fontSize: '0.85rem', transition: 'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
            <FaKey size={14} /> Change Password
          </button>

          {/* Logout */}
          <button
            onClick={handleLogout}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', borderRadius: 6, fontSize: '0.85rem', transition: 'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
            <FaSignOutAlt size={14} /> Logout
          </button>
        </div>
      </div>

      {/* Change password modal */}
      {showChangePass && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h5 style={{ fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><FaKey color={primary} /> Change Password</h5>
              <button onClick={() => setShowChangePass(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '1.1rem' }}><FaTimes /></button>
            </div>
            <form onSubmit={handleChangePassword}>
              {[
                ['Current Password', 'current_password'],
                ['New Password', 'new_password'],
                ['Confirm New Password', 'confirm_password'],
              ].map(([label, key]) => (
                <div key={key} style={{ marginBottom: 16 }}>
                  <label className="sms-label">{label}</label>
                  <input className="sms-input" type="password" value={passForm[key]}
                    onChange={e => setPassForm({ ...passForm, [key]: e.target.value })} required minLength={key !== 'current_password' ? 8 : 1} />
                </div>
              ))}
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button type="submit" disabled={saving} className="sms-btn sms-btn-primary" style={{ flex: 1, justifyContent: 'center', background: primary }}>
                  {saving ? 'Saving...' : 'Change Password'}
                </button>
                <button type="button" onClick={() => setShowChangePass(false)} className="sms-btn sms-btn-outline">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
